<script setup>
import {
  computed,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import { useRoute } from 'vue-router';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import MessageApi from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';
import { useMessageContext } from '../provider.js';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const route = useRoute();

let messageContext = null;
try {
  messageContext = useMessageContext();
} catch (e) {
  // outside message context
}

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const TRANSCRIPT_PREVIEW_LENGTH = 220;
const isTranscriptExpanded = ref(false);
const isTranscribing = ref(false);
const isCopied = ref(false);
const localTranscribedText = ref(attachment.transcribedText || '');
const showTranscriptBox = ref(!!(attachment.transcribedText || ''));

const activeTranscriptText = computed(() => {
  return attachment.transcribedText || localTranscribedText.value || '';
});


const hasTranscript = computed(() => {
  return activeTranscriptText.value.trim().length > 0;
});

const isTranscriptLong = computed(
  () => activeTranscriptText.value.length > TRANSCRIPT_PREVIEW_LENGTH
);

const displayedTranscript = computed(() => {
  const text = activeTranscriptText.value;
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    duration.value = el.duration;
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const onLoadedMetadata = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

onMounted(() => {
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};

const toggleOrTranscribe = async () => {
  if (hasTranscript.value) {
    showTranscriptBox.value = !showTranscriptBox.value;
    return;
  }

  const conversationId =
    messageContext?.conversationId?.value ||
    route.params.conversation_id ||
    route.params.conversationId;
  const messageId = messageContext?.id?.value || attachment.messageId;

  if (!conversationId || !messageId) {
    useAlert('Não foi possível identificar a conversa do áudio.');
    return;
  }

  try {
    isTranscribing.value = true;
    const response = await MessageApi.transcribeAudio(
      conversationId,
      messageId,
      attachment.id
    );

    if (response.data && response.data.text) {
      attachment.transcribedText = response.data.text;
      localTranscribedText.value = response.data.text;
      showTranscriptBox.value = true;
      isTranscriptExpanded.value = true;
      useAlert('Áudio transcrito com sucesso!');
    } else {
      useAlert('A IA não retornou texto para este áudio.');
    }
  } catch (error) {
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      'Erro ao transcrever áudio.';
    useAlert(`Falha ao transcrever: ${errorMsg}`);
  } finally {
    isTranscribing.value = false;
  }
};

const copyTranscript = async () => {
  if (!activeTranscriptText.value) return;
  try {
    await navigator.clipboard.writeText(activeTranscriptText.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (e) {
    useAlert('Erro ao copiar texto.');
  }
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        :title="isMuted ? 'Desmutar' : 'Mutar'"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        title="Baixar áudio"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
      <!-- Botão de Transcrição sob Demanda com IA -->
      <button
        class="p-0 border-0 size-8 grid place-content-center rounded-lg transition-colors cursor-pointer"
        :class="[
          hasTranscript
            ? 'text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/30'
            : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
        ]"
        :title="
          isTranscribing
            ? 'Transcrevendo áudio...'
            : hasTranscript
            ? 'Ver / Ocultar transcrição'
            : 'Transcrever áudio com IA'
        "
        :disabled="isTranscribing"
        @click="toggleOrTranscribe"
      >
        <Icon
          v-if="isTranscribing"
          class="size-4 animate-spin text-woot-600 dark:text-woot-400"
          icon="i-lucide-loader-2"
        />
        <Icon
          v-else-if="hasTranscript"
          class="size-4"
          icon="i-lucide-file-text"
        />
        <Icon
          v-else
          class="size-4"
          icon="i-lucide-sparkles"
        />
      </button>
    </div>

    <!-- Caixa Expansível de Transcrição -->
    <div
      v-if="hasTranscript && showTranscriptBox && showTranscribedText"
      class="text-n-slate-12 p-2.5 text-xs bg-n-alpha-1 dark:bg-n-alpha-2 rounded-lg w-full break-words border border-n-container flex flex-col gap-1.5 transition-all mt-1"
    >
      <div class="flex items-center justify-between border-b border-n-slate-6/30 pb-1">
        <span class="text-[11px] font-semibold text-woot-600 dark:text-woot-400 flex items-center gap-1">
          <Icon icon="i-lucide-sparkles" class="size-3" />
          <span>Transcrição do Áudio</span>
          <span class="opacity-60 text-[10px] font-normal text-n-slate-11">· Generated by Zéti Desk</span>
        </span>
        <button
          class="text-[11px] text-n-slate-11 hover:text-n-slate-12 flex items-center gap-1 bg-n-alpha-2 hover:bg-n-alpha-3 px-1.5 py-0.5 rounded transition-colors cursor-pointer border-0"
          :title="isCopied ? 'Copiado!' : 'Copiar transcrição'"
          @click="copyTranscript"
        >
          <Icon :icon="isCopied ? 'i-lucide-check' : 'i-lucide-copy'" class="size-3 text-woot-500" />
          <span>{{ isCopied ? 'Copiado!' : 'Copiar' }}</span>
        </button>
      </div>

      <div class="leading-relaxed text-n-slate-12 font-normal">
        {{ displayedTranscript }}
      </div>

      <button
        v-if="isTranscriptLong"
        class="self-start p-0 border-0 bg-transparent text-[11px] text-woot-600 dark:text-woot-400 hover:underline font-medium cursor-pointer"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{ isTranscriptExpanded ? 'Mostrar menos' : 'Mostrar mais' }}
      </button>
    </div>
  </div>
</template>
